import {MutableRefObject, useEffect, useMemo } from "react";
import { useState } from "react"
import {ScrollPositionPersist} from "../../services/persist/ScrollPositionPersist";
import {persistScrollSessionRepository} from "../../repositories/persist/persist-scroll-repository";


export interface IScrollPositionPersistLogic {
  onScrollHandler: () => void;
}

export const useScrollPositionPersist = <RefElement extends HTMLElement = HTMLDivElement>(scrollStorageKey: string, scrollRef: MutableRefObject<RefElement>): IScrollPositionPersistLogic => {

  const scrollPositionPersist = useMemo(
    () => new ScrollPositionPersist<RefElement>(scrollStorageKey, persistScrollSessionRepository),
    [scrollStorageKey]
  );

  //wait for a second before caring about scroll events, since so many scroll events are fired when the page is loading.
  const [isScrollEventIgnored, setIsScrollEventIgnored] = useState<boolean>(true);
  useEffect(() => {
    // start with scroll ignored for storage key
    setIsScrollEventIgnored(true);
    const waitTimeout = setTimeout(()=> setIsScrollEventIgnored(false), 1500);

    return () => {
      // attempt to clear timeout
      clearTimeout(waitTimeout);
    }
  }, [scrollStorageKey]);

  useEffect(() => {
    // restore persisted scroll position if available after .2 sec to allow extra time for loading
    setTimeout(()=> scrollPositionPersist.restoreScrollPosition(scrollRef.current), 200);
  }, [scrollStorageKey]);

  useEffect(() => {

    if (isScrollEventIgnored) {
      // not ready to monitor scroll events yet, return to exit
      return ;
    }

    // setup handlers for windows events
    const onWindowResize = () => {
      scrollPositionPersist.handleOnResize(scrollRef.current);
    }
    const onDeviceOrientation = () => {
      scrollPositionPersist.handleOnDeviceOrientation(scrollRef.current);
    }
    // add window event listeners
    window.addEventListener('resize', onWindowResize);
    window.addEventListener('deviceorientation', onDeviceOrientation);

    // return callback to unsubscribe from window event listeners
    return () => {
      window.removeEventListener('resize', onWindowResize);
      window.removeEventListener('deviceorientation', onDeviceOrientation);
    };
  }, [isScrollEventIgnored, scrollStorageKey]);



  const onScrollHandler = () => {
    if (isScrollEventIgnored) {
      // ignored, nothing for us to do
      // return to exit
      return;
    }
    scrollPositionPersist.handleOnScroll(scrollRef.current);
  };

  return {
    onScrollHandler,
  };
}
